import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { MessageTranslateService } from "../../../../shared/services/message.service";
import * as chardiActions from "../chardi/chardi.actions";
import { catchError, forkJoin, map, mergeMap, of, switchMap } from "rxjs";
import { ChardiService } from "../../services/chardi.service";
import { mapChardiConfigurationToChargingConfiguration, mapChargingConfigurationToChardiConfiguration, mapParkingPlaceToChardiOverview } from "../../utils/chardi.mapper";
import { RfidService } from "../../../rfids/services/rfid.service";

export class ChardiEffects {
    actions$: Actions = inject(Actions);
    store: Store = inject(Store);
    chardiService = inject(ChardiService);
    rfidService = inject(RfidService);
    messageService = inject(MessageTranslateService);

    loadChardiOverview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(chardiActions.loadChardiOverview),
            switchMap((requestData) =>
                this.chardiService.getParkingPlaceAndParkingSpots(requestData.chargerId, requestData.connectorId)
                    .pipe(
                        map((res) => {
                            const chardiOverview = mapParkingPlaceToChardiOverview(res, []);
                            return chardiActions.loadChardiOverviewSuccess({ chardiOverview });
                        }),
                        catchError((_) => {
                            return of(chardiActions.loadChardiOverviewFailure({ error: { errorMessage: 'ERROR' } }))
                        })
                    )
            )
        )
    );

    loadChardiConfigration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(chardiActions.loadChardiConfiguration),
            switchMap((requestData) =>
                this.chardiService.getChargingConfiguration(requestData.chargerId, requestData.connectorId)
                    .pipe(
                        map((res) => {
                            const chardiConfiguration = mapChargingConfigurationToChardiConfiguration(res);
                            return chardiActions.loadChardiConfigurationSuccess({ chardiConfiguration });
                        }),
                        catchError((_) => {
                            return of(chardiActions.loadChardiConfigurationFailure({ error: { errorMessage: 'ERROR' } }))
                        })
                    )
            )
        )
    );

    loadInitData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(chardiActions.loadInitData),
            switchMap((requestData) =>
                forkJoin({
                    chardiOverviewData:
                        this.chardiService.getParkingPlaceAndParkingSpots(requestData.chargerId, requestData.connectorId),
                    chardiConfigurationData:
                        this.chardiService.getChargingConfiguration(requestData.chargerId, requestData.connectorId)
                }).pipe(
                    mergeMap(({ chardiOverviewData, chardiConfigurationData }) => {
                        const macAddresses : string[] = chardiOverviewData
                            .modules
                            ?.filter(x => x.macAddress != null)
                            ?.map(x => x.macAddress!) ?? [];
                        return this.rfidService.getNumberPlates(macAddresses).pipe(
                            map((res) => {
                                if (!res.isSuccess){
                                    return {chardiOverviewData, chardiConfigurationData, numberPlates: [] }
                                }

                                return {chardiOverviewData, chardiConfigurationData, numberPlates: res.value?.data ?? []}
                            })
                        )
                    }),
                    map(({ chardiOverviewData, chardiConfigurationData, numberPlates }) => {
                        const chardiOverview = mapParkingPlaceToChardiOverview(chardiOverviewData, numberPlates);
                        const chardiConfiguration = mapChargingConfigurationToChardiConfiguration(chardiConfigurationData);
                        return chardiActions.loadInitDataSuccess({ chardiOverview, chardiConfiguration });
                    }),
                    catchError((_) => {
                        return of(chardiActions.loadInitDataSuccessFailure({ error: { errorMessage: 'ERROR' } }))
                    })
                )
            )
        )
    );

    saveChardiConfiguration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(chardiActions.saveChardiConfiguration),
            switchMap((req) =>
                this.chardiService.updateChargingConfiguration(mapChardiConfigurationToChargingConfiguration(req.request))
                    .pipe(
                        map((res) => {
                            const response = mapChargingConfigurationToChardiConfiguration(res);
                            return chardiActions.saveChardiConfigurationSuccess({ response });
                        }),
                        catchError((_) => {
                            return of(chardiActions.saveChardiConfigurationFailure({ error: { errorMessage: 'ERROR' } }))
                        })
                    )
            )
        )
    );

    removeFromQueue$ = createEffect(() =>
        this.actions$.pipe(
            ofType(chardiActions.removeFromQueue),
            switchMap((req) =>
                this.chardiService.removeFromQueue(req.parkingPlaceId, req.modulesIdToRemove)
                    .pipe(
                        map((_) => {
                            return chardiActions.removeFromQueueSuccess()
                        }),
                        catchError((_) => {
                            return of(chardiActions.removeFromQueueFailure({ error: { errorMessage: 'ERROR' } }))
                        })
                    )
            )
        )
    );

    addToQueue$ = createEffect(() =>
        this.actions$.pipe(
            ofType(chardiActions.addToQueue),
            switchMap((req) =>
                this.chardiService.addToQueue(req.parkingPlaceId, req.modulesIdToAdd)
                    .pipe(
                        map((_) => {
                            return chardiActions.addToQueueSuccess()
                        }),
                        catchError((_) => {
                            return of(chardiActions.addToQueueFailure({ error: { errorMessage: 'ERROR' } }))
                        })
                    )
            )
        )
    );

    stopTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(chardiActions.stopTransaction),
            switchMap((req) =>
                this.chardiService.stopTransaction(req.sessionId)
                    .pipe(
                        map((_) => chardiActions.stopTransactionSuccess()),
                        catchError((_) => of(chardiActions.stopTransactionFailure({ error: { errorMessage: 'ERROR' } })))
                    )
            )
        )
    );

    requestQueueReorder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(chardiActions.requestQueueReorder),
            switchMap((req) =>
                this.chardiService.requestQueueReorder(req.parkingPlaceId, req.modulesId)
                    .pipe(
                        map((_) => chardiActions.requestQueueReorderSuccess()),
                        catchError((_) => of(chardiActions.requestQueueReorderFailure({ error: { errorMessage: 'ERROR' } })))
                    )
            )
        )
    );

    updateDisplayNames$ = createEffect(() => 
        this.actions$.pipe(
            ofType(chardiActions.updateDisplayNames),
            switchMap((req) => 
                this.chardiService.updateDisplayNames(req.request)
                    .pipe(
                        map((_) => chardiActions.updateDisplayNamesSuccess({request: req.request})),
                        catchError((err) => of(chardiActions.updateDisplayNamesFailure({error: {errorMessage: err}})))
                    )
            )
        )
    )
}